import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SimpleModal } from '@/components/SimpleModal';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/utils';
import { 
  Store, 
  ShoppingBag, 
  Settings, 
  RefreshCw, 
  Check, 
  X, 
  ExternalLink,
  Truck,
  Clock,
  CheckCircle2,
  XCircle,
  Package,
  Printer,
  Eye,
  Play,
  Pause,
  Link2,
  Copy,
  AlertTriangle,
  Volume2,
  VolumeX,
  Bell,
  BellRing,
  Receipt,
  FileText,
  BarChart3,
  TrendingUp
} from 'lucide-react';

const API_URL = BACKEND_URL;

// Som de notificação para novos pedidos
const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleA4HR5TWmJpBAABPob+RcC0NDmOq2LRvEgAOfrjlnWIXAAZst+KqcyIACXC03q92JQAMZK7ZsngqABBgqde0ei4AFVun1LmAMgAdXKPRvIU2ACBaoM6+iDoAI1edysKLPgAlVJrGxo9BAChRl8LJklUAK06TvsyUWgAtS5C7z5ddAC9IjLjRml8AMUSItNObYgAzQIS00p1kADU8gbHUn2YAN0J9rdagaAA4Pnqp2KJqADo7dqbZpGwAOzdzotukbgA8NHCg3KZvAD0xbZ7dqHEAPy5pnN6qcwA/K2aa36xzAEEoY5jgrHUAQiVgl+GudwBDI12V4q95AEQgWpPjsHsARR5Xkub/fgBGG1SQ5bKAAEcYUY7ms4IASCVP';

// Ícones dos marketplaces
const MarketplaceIcon = ({ marketplace, size = 'md' }) => {
  const icons = {
    ze_delivery: '🍺',
    ifood: '🍔',
    rappi: '🛵',
    mercadolivre: '🛒',
    shopee: '🧡',
    tiktok_shop: '🎵',
    shein: '👗',
    food99: '🍽️'
  };
  const sizeClass = size === 'lg' ? 'text-4xl' : size === 'sm' ? 'text-lg' : 'text-2xl';
  return <span className={sizeClass}>{icons[marketplace] || '📦'}</span>;
};

// Status badges
const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'Pendente', color: 'bg-yellow-500', icon: Clock },
    accepted: { label: 'Aceito', color: 'bg-blue-500', icon: Check },
    preparing: { label: 'Preparando', color: 'bg-purple-500', icon: Package },
    ready: { label: 'Pronto', color: 'bg-emerald-500', icon: CheckCircle2 },
    delivered: { label: 'Entregue', color: 'bg-slate-500', icon: Truck },
    cancelled: { label: 'Cancelado', color: 'bg-red-500', icon: XCircle }
  };
  const { label, color, icon: Icon } = config[status] || config.pending;
  return (
    <Badge className={`${color} text-white flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

export const MarketplacesPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [marketplaces, setMarketplaces] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState({});
  
  // Som e notificações
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('marketplace_sound') !== 'false';
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastPendingCount, setLastPendingCount] = useState(0);
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(() => {
    return localStorage.getItem('marketplace_autoprint') === 'true';
  });
  const knownOrderIdsRef = useRef(null);
  const autoPrintRef = useRef(autoPrintEnabled);
  const audioRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  
  // Modal de configuração
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [configForm, setConfigForm] = useState({
    api_key: '',
    api_secret: '',
    store_id: '',
    enabled: false,
    auto_accept_orders: false,
    sync_products: true,
    sync_stock: true
  });
  const [saving, setSaving] = useState(false);
  
  // Modal de pedido
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [printContent, setPrintContent] = useState('');
  
  // Emissão de Nota Fiscal
  const [showEmitirNotaModal, setShowEmitirNotaModal] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState('nfce');
  const [emitindoNota, setEmitindoNota] = useState(false);

  // Filtro de status
  const [statusFilter, setStatusFilter] = useState('all');
  const [marketplaceFilter, setMarketplaceFilter] = useState('all');

  // Relatório por canal
  const [report, setReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState('month');
  const [reportLoading, setReportLoading] = useState(false);

  const loadReport = useCallback(async (periodArg) => {
    const period = periodArg || reportPeriod;
    setReportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/marketplace/report?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setReport(data);
    } catch (e) {
      console.error('Erro ao carregar relatório:', e);
    } finally {
      setReportLoading(false);
    }
  }, [reportPeriod]);

  // Tocar som de notificação
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Erro ao tocar som:', e));
    }
  }, [soundEnabled]);

  // Toggle som
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('marketplace_sound', String(newValue));
    if (newValue) {
      // Tocar som de teste
      playNotificationSound();
      toast({ title: 'Som ativado', description: 'Você receberá alertas sonoros para novos pedidos' });
    } else {
      toast({ title: 'Som desativado', description: 'Alertas sonoros desabilitados' });
    }
  };

  // Toggle impressão automática na cozinha
  const toggleAutoPrint = () => {
    const newValue = !autoPrintEnabled;
    setAutoPrintEnabled(newValue);
    autoPrintRef.current = newValue;
    localStorage.setItem('marketplace_autoprint', String(newValue));
    toast({
      title: newValue ? '🖨️ Impressão automática ativada' : 'Impressão automática desativada',
      description: newValue ? 'Novos pedidos serão impressos na cozinha automaticamente' : 'Você imprimirá os pedidos manualmente'
    });
  };

  // Impressão silenciosa via iframe (não é bloqueada por pop-up)
  const printViaIframe = useCallback((htmlText) => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<html><head><title>Pedido</title><style>body{font-family:'Courier New',monospace;font-size:12px;white-space:pre;margin:0;padding:4px;}</style></head><body>${htmlText}</body></html>`);
    doc.close();
    setTimeout(() => {
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) { /* noop */ }
      setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 1500);
    }, 350);
  }, []);

  // Buscar conteúdo e imprimir automaticamente um pedido
  const autoPrintOrder = useCallback(async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/marketplace/orders/${orderId}/print`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.relatorio) printViaIframe(data.relatorio);
    } catch (e) {
      console.error('Erro na impressão automática:', e);
    }
  }, [printViaIframe]);

  // Carregar dados
  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const [mpRes, configRes, ordersRes] = await Promise.all([
        fetch(`${API_URL}/api/marketplace/list`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/marketplace/config`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/marketplace/orders?limit=100`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      const mpData = await mpRes.json();
      const configData = await configRes.json();
      const ordersData = await ordersRes.json();
      
      const ordersList = ordersData.orders || [];
      setMarketplaces(mpData.marketplaces || {});
      setConfigs(configData.configs || []);
      setOrders(ordersList);
      
      const newStats = ordersData.stats || {};
      const newPendingCount = newStats.pending || 0;
      
      // Detectar pedidos NOVOS (por id) para impressão automática na cozinha
      const currentPendingIds = ordersList.filter(o => o.status === 'pending').map(o => o.id);
      if (knownOrderIdsRef.current === null) {
        // Primeira carga: apenas memoriza, não imprime os já existentes
        knownOrderIdsRef.current = new Set(ordersList.map(o => o.id));
      } else {
        const novos = currentPendingIds.filter(id => !knownOrderIdsRef.current.has(id));
        if (novos.length > 0 && autoPrintRef.current) {
          novos.forEach(id => autoPrintOrder(id));
        }
        knownOrderIdsRef.current = new Set(ordersList.map(o => o.id));
      }
      
      // Verificar se há novos pedidos pendentes
      if (newPendingCount > lastPendingCount && lastPendingCount > 0) {
        playNotificationSound();
        toast({ 
          title: '🔔 Novo Pedido!', 
          description: `Você tem ${newPendingCount} pedido(s) pendente(s)`,
          variant: 'default'
        });
      }
      
      setOrderStats(newStats);
      setLastPendingCount(newPendingCount);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [lastPendingCount, playNotificationSound, toast, autoPrintOrder]);

  // Carregar dados iniciais
  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh a cada 15 segundos
  useEffect(() => {
    if (autoRefresh) {
      refreshIntervalRef.current = setInterval(() => {
        loadData(false);
      }, 15000);
    }
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [autoRefresh, loadData]);

  // Criar pedido de teste
  const createTestOrder = async (mp) => {
    try {
      const token = localStorage.getItem('token');
      const url = mp ? `${API_URL}/api/marketplace/test-order?marketplace=${mp}` : `${API_URL}/api/marketplace/test-order`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ 
          title: '🧪 Pedido de Teste Criado!', 
          description: `${data.message} - Total: R$ ${data.order.total.toFixed(2)}`
        });
        // Recarregar dados para mostrar o novo pedido
        setTimeout(() => loadData(false), 500);
      }
    } catch (error) {
      toast({ title: 'Erro', description: 'Erro ao criar pedido de teste', variant: 'destructive' });
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copiado!', description: 'URL do webhook copiada' });
    } catch (e) {
      toast({ title: 'Erro', description: 'Não foi possível copiar', variant: 'destructive' });
    }
  };

  // Abrir modal de configuração
  const openConfigModal = (config) => {
    setSelectedMarketplace(config);
    setWebhookInfo(config.webhook_url || null);
    setConfigForm({
      api_key: '',
      api_secret: '',
      store_id: config.store_id || '',
      enabled: config.enabled || false,
      auto_accept_orders: config.auto_accept_orders || false,
      sync_products: config.sync_products !== false,
      sync_stock: config.sync_stock !== false
    });
    setShowConfigModal(true);
  };

  // Salvar configuração
  const handleSaveConfig = async () => {
    if (!selectedMarketplace) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketplace/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          marketplace: selectedMarketplace.marketplace,
          ...configForm
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: 'Sucesso', description: data.message });
        if (data.webhook_url) setWebhookInfo(data.webhook_url);
        loadData(false);
      } else {
        toast({ title: 'Erro', description: data.detail || 'Erro ao salvar', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Sincronizar produtos
  const syncProducts = async (marketplace) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketplace/${marketplace}/sync-products`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: 'Sucesso', description: data.message });
      } else {
        toast({ title: 'Erro', description: data.detail || 'Erro ao sincronizar', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  // Atualizar status do pedido
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketplace/orders/${orderId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Se marcou como entregue, gerar fluxo de caixa
        if (newStatus === 'delivered') {
          try {
            const cashflowResponse = await fetch(`${API_URL}/api/orders/marketplace/${orderId}/finalize`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const cashflowData = await cashflowResponse.json();
            
            if (cashflowData.success) {
              toast({ 
                title: '✅ Pedido Finalizado', 
                description: `Valor R$ ${cashflowData.amount?.toFixed(2) || '0,00'} registrado no caixa` 
              });
            } else {
              toast({ title: 'Sucesso', description: `Status atualizado para ${newStatus}` });
            }
          } catch (e) {
            console.error('Erro ao gerar fluxo de caixa:', e);
            toast({ title: 'Sucesso', description: `Status atualizado para ${newStatus}` });
          }
        } else {
          toast({ title: 'Sucesso', description: `Status atualizado para ${newStatus}` });
        }
        loadData();
        if (selectedOrder?.id === orderId) {
          setSelectedOrder({ ...selectedOrder, status: newStatus });
        }
      }
    } catch (error) {
      toast({ title: 'Erro', description: error.message, variant: 'destructive' });
    }
  };

  // Ver detalhes do pedido
  const viewOrder = async (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
    
    // Carregar impressão
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketplace/orders/${order.id}/print`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPrintContent(data.relatorio || '');
    } catch (error) {
      console.error('Erro ao carregar impressão');
    }
  };

  // Emitir Nota Fiscal do pedido marketplace
  const emitirNotaFiscal = async () => {
    if (!selectedOrder) return;
    
    setEmitindoNota(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/marketplace/orders/${selectedOrder.id}/emitir-nota`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tipo_documento: tipoDocumento })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const tipoNota = tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e';
        toast({ 
          title: 'Sucesso!', 
          description: `${tipoNota} emitida com sucesso!`
        });
        setShowEmitirNotaModal(false);
        loadData(); // Recarregar pedidos
      } else {
        toast({ 
          title: 'Erro', 
          description: data.message || 'Erro ao emitir nota fiscal',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({ 
        title: 'Erro', 
        description: 'Erro ao emitir nota fiscal',
        variant: 'destructive'
      });
    } finally {
      setEmitindoNota(false);
    }
  };

  // Imprimir pedido
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head><title>Pedido</title>
          <style>body { font-family: 'Courier New', monospace; font-size: 12px; white-space: pre; }</style>
        </head>
        <body>${printContent}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', { 
      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="marketplaces-page">
      {/* Audio element para notificação */}
      <audio ref={audioRef} src={NOTIFICATION_SOUND_URL} preload="auto" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Store className="w-8 h-8 text-emerald-600" />
            Marketplaces
          </h1>
          <p className="text-slate-500 mt-1">Integração com Zé Delivery, iFood, Rappi, Mercado Livre, Shopee, TikTok Shop, Shein e 99 Food</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Indicador de pedidos pendentes */}
          {(orderStats.pending || 0) > 0 && (
            <div className="flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-lg animate-pulse">
              <BellRing className="w-5 h-5" />
              <span className="font-bold">{orderStats.pending} pendente(s)</span>
            </div>
          )}
          
          {/* Controle de som */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={toggleSound}
            title={soundEnabled ? 'Desativar som' : 'Ativar som'}
            data-testid="toggle-sound"
          >
            {soundEnabled ? <Volume2 className="w-4 h-4 text-emerald-600" /> : <VolumeX className="w-4 h-4 text-slate-400" />}
          </Button>

          {/* Impressão automática na cozinha */}
          <Button
            variant={autoPrintEnabled ? "default" : "outline"}
            size="sm"
            onClick={toggleAutoPrint}
            className={autoPrintEnabled ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            title={autoPrintEnabled ? 'Impressão automática ativada' : 'Impressão automática desativada'}
            data-testid="toggle-autoprint"
          >
            <Printer className="w-4 h-4 mr-2" />
            {autoPrintEnabled ? 'Impressão auto' : 'Impressão'}
          </Button>
          
          {/* Auto refresh */}
          <Button 
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-emerald-600 hover:bg-emerald-700" : ""}
            data-testid="toggle-autorefresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          
          <Button onClick={() => loadData(true)} variant="outline" data-testid="btn-atualizar">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </div>

      <Tabs defaultValue="integracoes" className="space-y-6">
        <TabsList>
          <TabsTrigger value="integracoes" data-testid="tab-integracoes">
            <Link2 className="w-4 h-4 mr-2" />
            Integrações
          </TabsTrigger>
          <TabsTrigger value="pedidos" data-testid="tab-pedidos">
            <ShoppingBag className="w-4 h-4 mr-2" />
            Pedidos
            {(orderStats.pending || 0) > 0 && (
              <Badge className="ml-2 bg-red-500 animate-pulse">{orderStats.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="relatorio" data-testid="tab-relatorio" onClick={() => loadReport()}>
            <BarChart3 className="w-4 h-4 mr-2" />
            Relatório
          </TabsTrigger>
        </TabsList>

        {/* Aba Integrações */}
        <TabsContent value="integracoes">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {configs.map((config) => (
              <Card key={config.marketplace} className={`relative ${config.enabled ? 'border-emerald-500 border-2' : ''}`}>
                {config.enabled && (
                  <div className="absolute -top-2 -right-2">
                    <Badge className="bg-emerald-500">Ativo</Badge>
                  </div>
                )}
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <MarketplaceIcon marketplace={config.marketplace} size="lg" />
                    <div>
                      <CardTitle className="text-lg">{config.info?.name}</CardTitle>
                      <CardDescription className="text-xs">{config.info?.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Credenciais:</span>
                      <span className={config.has_credentials ? 'text-emerald-600' : 'text-slate-400'}>
                        {config.has_credentials ? '✓ Configurado' : '○ Não configurado'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Auto aceitar:</span>
                      <span>{config.auto_accept_orders ? 'Sim' : 'Não'}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Sincronizar produtos:</span>
                      <span>{config.sync_products ? 'Sim' : 'Não'}</span>
                    </div>
                    
                    <div className="flex gap-2 pt-3 border-t">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => openConfigModal(config)}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Configurar
                      </Button>
                      {config.enabled && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => syncProducts(config.marketplace)}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Aba Pedidos */}
        <TabsContent value="pedidos">
          {/* Stats com filtro clicável */}
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6">
            <Card 
              className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'all' ? 'ring-2 ring-emerald-500' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              <CardContent className="p-4 text-center">
                <p className="text-sm text-slate-500 mb-1">Todos</p>
                <p className="text-2xl font-bold">{orders.length}</p>
              </CardContent>
            </Card>
            {['pending', 'accepted', 'preparing', 'ready', 'delivered', 'cancelled'].map(status => (
              <Card 
                key={status} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === status ? 'ring-2 ring-emerald-500' : ''}`}
                onClick={() => setStatusFilter(status)}
              >
                <CardContent className="p-4 text-center">
                  <StatusBadge status={status} />
                  <p className="text-2xl font-bold mt-2">{orderStats[status] || 0}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Lista de Pedidos */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  Pedidos {statusFilter !== 'all' && `(${statusFilter})`}
                  {(orderStats.pending || 0) > 0 && (
                    <span className="flex items-center gap-1 text-sm font-normal text-red-600">
                      <Bell className="w-4 h-4 animate-bounce" />
                      {orderStats.pending} aguardando
                    </span>
                  )}
                </CardTitle>
                <div className="flex items-center gap-3">
                  <select
                    value={marketplaceFilter}
                    onChange={(e) => setMarketplaceFilter(e.target.value)}
                    className="h-9 px-3 border border-slate-300 rounded-md bg-white text-sm"
                    data-testid="orders-marketplace-filter"
                  >
                    <option value="all">Todos os canais</option>
                    {Object.entries(marketplaces).map(([mpId, mpInfo]) => (
                      <option key={mpId} value={mpId}>{mpInfo.icon} {mpInfo.name}</option>
                    ))}
                  </select>
                  <p className="text-sm text-slate-400">
                    Atualização automática: {autoRefresh ? 'a cada 15s' : 'desativada'}
                  </p>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={createTestOrder}
                    data-testid="btn-test-order"
                    title="Criar pedido de teste para simular recebimento"
                  >
                    🧪 Testar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {orders.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhum pedido de marketplace</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders
                    .filter(order => statusFilter === 'all' || order.status === statusFilter)
                    .filter(order => marketplaceFilter === 'all' || order.marketplace === marketplaceFilter)
                    .map((order) => (
                    <div 
                      key={order.id} 
                      className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                        order.status === 'pending' 
                          ? 'bg-yellow-50 hover:bg-yellow-100 border-l-4 border-yellow-500' 
                          : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      onClick={() => viewOrder(order)}
                      data-testid={`order-${order.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <MarketplaceIcon marketplace={order.marketplace} size="md" />
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            #{order.external_order_id}
                            {order.status === 'pending' && (
                              <span className="text-xs text-yellow-600 animate-pulse">● NOVO</span>
                            )}
                          </p>
                          <p className="text-sm text-slate-500">{order.customer_name}</p>
                          {order.notes && (
                            <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                              <AlertTriangle className="w-3 h-3" />
                              {order.notes.substring(0, 50)}...
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-lg">{formatCurrency(order.total)}</p>
                          <p className="text-xs text-slate-400">{formatDate(order.created_at)}</p>
                        </div>
                        <StatusBadge status={order.status} />
                        {order.status === 'pending' && (
                          <Button 
                            size="sm" 
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateOrderStatus(order.id, 'accepted');
                            }}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Aceitar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Relatório por Canal */}
        <TabsContent value="relatorio">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Vendas por Canal {report?.period_label ? `• ${report.period_label}` : ''}
            </h2>
            <div className="flex items-center gap-2">
              <select
                value={reportPeriod}
                onChange={(e) => { setReportPeriod(e.target.value); loadReport(e.target.value); }}
                className="h-9 px-3 border border-slate-300 rounded-md bg-white text-sm"
                data-testid="report-period-filter"
              >
                <option value="month">Mês atual</option>
                <option value="last30">Últimos 30 dias</option>
                <option value="all">Todo o período</option>
              </select>
              <Button size="sm" variant="outline" onClick={() => loadReport()} data-testid="report-refresh">
                <RefreshCw className={`w-4 h-4 ${reportLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Totais */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Faturamento total</p>
                <p className="text-2xl font-bold text-emerald-600" data-testid="report-grand-total">{formatCurrency(report?.grand_total || 0)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total de pedidos</p>
                <p className="text-2xl font-bold">{report?.grand_count || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Ticket médio</p>
                <p className="text-2xl font-bold">{formatCurrency((report?.grand_count ? (report.grand_total / report.grand_count) : 0))}</p>
              </CardContent>
            </Card>
          </div>

          {/* Ranking por canal */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Comparativo entre marketplaces</CardTitle>
            </CardHeader>
            <CardContent>
              {reportLoading ? (
                <p className="text-center py-8 text-slate-400">Carregando...</p>
              ) : (!report || report.report.length === 0) ? (
                <div className="text-center py-8 text-slate-400" data-testid="report-empty">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Nenhuma venda de marketplace no período</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {report.report.map((r) => (
                    <div key={r.marketplace} data-testid={`report-row-${r.marketplace}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium flex items-center gap-2">
                          <span className="text-xl">{r.icon}</span> {r.name}
                          <span className="text-xs text-slate-400">({r.count} pedido{r.count !== 1 ? 's' : ''})</span>
                        </span>
                        <span className="font-bold">{formatCurrency(r.total)}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                        <div className="h-3 rounded-full transition-all" style={{ width: `${r.percent}%`, backgroundColor: r.color }} />
                      </div>
                      <div className="flex justify-between text-xs text-slate-400 mt-1">
                        <span>{r.percent}% do faturamento</span>
                        <span>Ticket médio: {formatCurrency(r.ticket_medio)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Configuração */}
      <SimpleModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title={`Configurar ${selectedMarketplace?.info?.name || 'Marketplace'}`}
      >
        {selectedMarketplace && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
              <MarketplaceIcon marketplace={selectedMarketplace.marketplace} size="lg" />
              <div>
                <p className="font-medium">{selectedMarketplace.info?.name}</p>
                <p className="text-sm text-slate-500">{selectedMarketplace.info?.description}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>API Key / Token</Label>
                <Input
                  type="password"
                  value={configForm.api_key}
                  onChange={(e) => setConfigForm({ ...configForm, api_key: e.target.value })}
                  placeholder="Sua chave de API..."
                />
              </div>
              
              {selectedMarketplace.info?.requires?.includes('api_secret') && (
                <div className="col-span-2">
                  <Label>API Secret</Label>
                  <Input
                    type="password"
                    value={configForm.api_secret}
                    onChange={(e) => setConfigForm({ ...configForm, api_secret: e.target.value })}
                    placeholder="Seu secret..."
                  />
                </div>
              )}
              
              {selectedMarketplace.info?.requires?.includes('store_id') && (
                <div className="col-span-2">
                  <Label>ID da Loja</Label>
                  <Input
                    value={configForm.store_id}
                    onChange={(e) => setConfigForm({ ...configForm, store_id: e.target.value })}
                    placeholder="ID ou código da loja..."
                  />
                </div>
              )}
            </div>

            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Ativar integração</p>
                  <p className="text-sm text-slate-500">Receber pedidos deste marketplace</p>
                </div>
                <Switch
                  checked={configForm.enabled}
                  onCheckedChange={(checked) => setConfigForm({ ...configForm, enabled: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Aceitar pedidos automaticamente</p>
                  <p className="text-sm text-slate-500">Pedidos serão aceitos sem confirmação manual</p>
                </div>
                <Switch
                  checked={configForm.auto_accept_orders}
                  onCheckedChange={(checked) => setConfigForm({ ...configForm, auto_accept_orders: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Sincronizar produtos</p>
                  <p className="text-sm text-slate-500">Enviar catálogo para o marketplace</p>
                </div>
                <Switch
                  checked={configForm.sync_products}
                  onCheckedChange={(checked) => setConfigForm({ ...configForm, sync_products: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Sincronizar estoque</p>
                  <p className="text-sm text-slate-500">Atualizar estoque em tempo real</p>
                </div>
                <Switch
                  checked={configForm.sync_stock}
                  onCheckedChange={(checked) => setConfigForm({ ...configForm, sync_stock: checked })}
                />
              </div>
            </div>

            {/* Webhook — conexão self-service (cada cliente cola no painel do marketplace) */}
            {webhookInfo && (() => {
              const fullWebhook = webhookInfo.startsWith('http') ? webhookInfo : `${API_URL}${webhookInfo}`;
              return (
              <div className="space-y-2 pt-4 border-t" data-testid="webhook-section">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-emerald-600" />
                  <p className="font-medium">URL de Webhook (para receber pedidos)</p>
                </div>
                <p className="text-sm text-slate-500">
                  Cole esta URL no painel de integrações do {selectedMarketplace.info?.name}. É exclusiva da sua loja — os pedidos chegam automaticamente aqui.
                </p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={fullWebhook} className="text-xs font-mono" data-testid="webhook-url-input" />
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(fullWebhook)} data-testid="copy-webhook-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-1"
                  onClick={() => createTestOrder(selectedMarketplace.marketplace)}
                  data-testid="test-connection-btn"
                >
                  🧪 Enviar pedido de teste deste canal
                </Button>
              </div>
              );
            })()}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowConfigModal(false)}>
                {webhookInfo ? 'Fechar' : 'Cancelar'}
              </Button>
              <Button onClick={handleSaveConfig} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Salvar
              </Button>
            </div>
          </div>
        )}
      </SimpleModal>

      {/* Modal de Pedido */}
      <SimpleModal
        open={showOrderModal}
        onClose={() => setShowOrderModal(false)}
        title={`Pedido #${selectedOrder?.external_order_id || ''}`}
        className="max-w-2xl"
      >
        {selectedOrder && (
          <div className="space-y-4">
            {/* Header do pedido */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <MarketplaceIcon marketplace={selectedOrder.marketplace} size="lg" />
                <div>
                  <p className="font-medium">{marketplaces[selectedOrder.marketplace]?.name}</p>
                  <p className="text-sm text-slate-500">{formatDate(selectedOrder.created_at)}</p>
                </div>
              </div>
              <StatusBadge status={selectedOrder.status} />
            </div>

            {/* Cliente */}
            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-2">Cliente</h3>
              <p>{selectedOrder.customer_name}</p>
              {selectedOrder.customer_phone && <p className="text-sm text-slate-500">{selectedOrder.customer_phone}</p>}
              {selectedOrder.customer_address && <p className="text-sm text-slate-500">{selectedOrder.customer_address}</p>}
              {selectedOrder.notes && (
                <p className="text-sm text-amber-600 mt-2 p-2 bg-amber-50 rounded">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  {selectedOrder.notes}
                </p>
              )}
            </div>

            {/* Itens */}
            <div className="p-4 border rounded-lg">
              <h3 className="font-medium mb-2">Itens</h3>
              <div className="space-y-2">
                {selectedOrder.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.quantity}x {item.name}</span>
                    <span className="font-medium">{formatCurrency(item.total || item.quantity * item.unit_price)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t mt-3 pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedOrder.subtotal)}</span>
                </div>
                {selectedOrder.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Taxa de entrega</span>
                    <span>{formatCurrency(selectedOrder.delivery_fee)}</span>
                  </div>
                )}
                {selectedOrder.discount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Desconto</span>
                    <span>-{formatCurrency(selectedOrder.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span>{formatCurrency(selectedOrder.total)}</span>
                </div>
              </div>
            </div>

            {/* Pagamento */}
            <div className="p-3 bg-slate-100 rounded-lg text-center">
              <span className="text-sm text-slate-500">Pagamento: </span>
              <span className="font-medium">{selectedOrder.payment_method?.toUpperCase()}</span>
            </div>

            {/* Ações de Status */}
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              {selectedOrder.status === 'pending' && (
                <>
                  <Button onClick={() => updateOrderStatus(selectedOrder.id, 'accepted')} className="bg-blue-600 hover:bg-blue-700">
                    <Check className="w-4 h-4 mr-1" /> Aceitar
                  </Button>
                  <Button onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')} variant="destructive">
                    <X className="w-4 h-4 mr-1" /> Recusar
                  </Button>
                </>
              )}
              {selectedOrder.status === 'accepted' && (
                <Button onClick={() => updateOrderStatus(selectedOrder.id, 'preparing')} className="bg-purple-600 hover:bg-purple-700">
                  <Package className="w-4 h-4 mr-1" /> Preparando
                </Button>
              )}
              {selectedOrder.status === 'preparing' && (
                <Button onClick={() => updateOrderStatus(selectedOrder.id, 'ready')} className="bg-emerald-600 hover:bg-emerald-700">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Pronto
                </Button>
              )}
              {selectedOrder.status === 'ready' && (
                <Button onClick={() => updateOrderStatus(selectedOrder.id, 'delivered')} className="bg-slate-600 hover:bg-slate-700">
                  <Truck className="w-4 h-4 mr-1" /> Entregue
                </Button>
              )}
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-1" /> Imprimir
              </Button>
              {/* Botão Emitir Nota - aparece para pedidos aceitos ou entregues */}
              {(selectedOrder.status === 'accepted' || selectedOrder.status === 'delivered' || selectedOrder.status === 'ready') && !selectedOrder.nfce_key && !selectedOrder.nfe_key && (
                <Button 
                  variant="outline" 
                  className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                  onClick={() => setShowEmitirNotaModal(true)}
                >
                  <Receipt className="w-4 h-4 mr-1" /> Emitir Nota
                </Button>
              )}
            </div>
          </div>
        )}
      </SimpleModal>
      
      {/* Modal Emitir Nota Fiscal */}
      {showEmitirNotaModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEmitirNotaModal(false)}>
          <div 
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-slate-900 mb-2">Emitir Documento Fiscal</h2>
            <p className="text-slate-600 mb-6">Pedido #{selectedOrder.order_number || selectedOrder.id?.substring(0, 8).toUpperCase()}</p>
            
            <div className="space-y-3 mb-6">
              <button
                type="button"
                onClick={() => setTipoDocumento('nfce')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  tipoDocumento === 'nfce' 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Receipt className="w-5 h-5 text-green-600" />
                  <div>
                    <span className="font-semibold block">NFC-e - Nota Fiscal do Consumidor</span>
                    <span className="text-sm text-slate-500">Para vendas a consumidor final</span>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setTipoDocumento('nfe')}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                  tipoDocumento === 'nfe' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <div>
                    <span className="font-semibold block">NF-e - Nota Fiscal Eletrônica</span>
                    <span className="text-sm text-slate-500">Para vendas entre empresas (B2B)</span>
                  </div>
                </div>
              </button>
            </div>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowEmitirNotaModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={emitirNotaFiscal} 
                disabled={emitindoNota}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {emitindoNota ? 'Emitindo...' : `Emitir ${tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e'}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
